import {
    labelPositionTypeMap,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/map';
import {
    convertToDocumentElement, createDocumentWithChartTextProps,
    getTextStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/text';
import { getTextLayout } from '@tencent/alloy-editor';
import type {
    ChartUIDataItem,
    ChartUILabel,
    GemLabelConf,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type { IChartManualLayout } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import { GeometryLabelCfg } from '@tencent/chart/lib/antv/g2/interface';
import {
    generateCustomStyles,
    getOriginStyle,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';

export class DataLabel {
    /**
     * 以field映射回调的形式，生成Chart库所需配置中的数据标签配置
     */
    public static getFiledMappingConfig(data: ChartUIDataItem[], subChart: IBaseSubChartProperty) {
        return {
            fields: ['id'],
            callback: (id) => {
                const chartDataItem = data.find(item => item.id === id);
                if (!chartDataItem) return {};
                return this.getLabelConfig(chartDataItem, subChart) as GeometryLabelCfg;
            },
            cfg: {
                layout: {
                    type: 'auto-adjust',
                    cfg: {
                        // action: this.getReLayoutLabelContent,
                    },
                },
            },
        };
    }

    protected static getReLayoutLabelContent(text: string, width: number, style: any): string {
        const editorElement = convertToDocumentElement(text, style);
        const textLayoutBox = getTextLayout(editorElement, width);

        // @ts-ignore
        const lineBoxes = textLayoutBox._children[0]._children;
        const reLayoutContent = lineBoxes.map(line => line._children
        // 这里要replace的原因是，label的Content 目前是将多个paragraph转成了一个text（构造了一个简单的text）来排版，
        // 会在text中加\n来构成换行，而排版每一行都会有一个lineBox，所以可以去掉所有的换行符，直接将非换行的文本进行\n拼接。
        // 否则会存在\n重复拼接，导致多次换行的情况
            .map(textBox => textBox.text.replace('\n', '')).join('')).join('\n');

        return reLayoutContent;
    }

    /**
     * 根据对应的数据点Label属性，生成返回给Chart库的数据标签配置
     */
    protected static getLabelConfig(chartDataItem: ChartUIDataItem, subChart: IBaseSubChartProperty) {
        const { label } = chartDataItem;
        if (typeof label === 'boolean') {
            return !label ? null : true;
        }

        return label as GemLabelConf;
    }

    public constructor(
        protected dataLabelProperty: IDataLabelProperty,
        protected context: ChartContext,
    ) {}

    /**
     * 根据Property的数据标签数据，生成最后Chart库会使用的label相关属性
     */
    public getConfig(): ChartUILabel {
        const { content, dataItem, layout, labelPosition, text } = this.dataLabelProperty;
        const docElement = createDocumentWithChartTextProps(this.dataLabelProperty);
        const chartModelStyle = this.context.getChartModel().props.style;
        const labelWidth = layout?.width ? layout.width * (chartModelStyle?.width || 0) : undefined;
        const labelHeight = layout?.height ? layout.height * (chartModelStyle?.height || 0) : undefined;

        const label: ChartUILabel = {
            content,
            position: this.getLabelPosition(dataItem.value, this.dataLabelProperty, layout),
            originPosition: labelPosition,
            style: {
                ...getTextStyle(this.dataLabelProperty, {}),
                ...(text && getTextStyle(
                    text,
                    { defaultProps: this.dataLabelProperty },
                )),
                width: labelWidth,
                height: labelHeight,
                maxWidth: labelWidth,
                customStyles: {
                    text: docElement,
                    ...generateCustomStyles(getOriginStyle(this.dataLabelProperty.props)),
                },
            },
            customOffset: {
                x: layout?.left,
                y: layout?.top,
            },
            // labelLine: dataPointLabel.showLeaderLines,
            // g2的labelLine适配很差，先都不显示
            labelLine: false,
            autoRotate: false,
        };

        if (this.dataLabelProperty.formatCodeColor) {
            label.style.fill = this.dataLabelProperty.formatCodeColor;
            if (!text) { // 同时去变更非自定义docElement的color
                const firstTextRunStyle = docElement.children[0]?.children[0];
                if (firstTextRunStyle) firstTextRunStyle.color = this.dataLabelProperty.formatCodeColor;
            }
        }

        return label;
    }

    protected getLabelPosition(value: number, dataPointLabel: IDataLabelProperty, layout?: IChartManualLayout) {
        if (layout) {
            return '';
        }

        return dataPointLabel.labelPosition ? labelPositionTypeMap[dataPointLabel.labelPosition] : '';
    }

    protected get subChart() {
        return this.context.getSubChartModel().getSubChartProperty();
    }
}


